/**
 * Leviathan - Lead Form Page
 * Quasar Prime LLC
 *
 * Create or edit a lead
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

import { API_URL } from '@/config/api';

const LEAD_SOURCES = [
  { value: 'WEBSITE', label: 'Website' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'COLD_CALL', label: 'Cold Call' },
  { value: 'TRADE_SHOW', label: 'Trade Show' },
  { value: 'ADVERTISEMENT', label: 'Advertisement' },
  { value: 'SOCIAL_MEDIA', label: 'Social Media' },
  { value: 'EXISTING_CUSTOMER', label: 'Existing Customer' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'OTHER', label: 'Other' },
];

const LEAD_STATUSES = [
  { value: 'NEW', label: 'New' },
  { value: 'CONTACTED', label: 'Contacted' },
  { value: 'QUALIFIED', label: 'Qualified' },
  { value: 'PROPOSAL', label: 'Proposal' },
  { value: 'NEGOTIATION', label: 'Negotiation' },
  { value: 'WON', label: 'Won' },
  { value: 'LOST', label: 'Lost' },
  { value: 'DORMANT', label: 'Dormant' },
];

interface FormData {
  companyName: string;
  contactFirstName: string;
  contactLastName: string;
  contactTitle: string;
  email: string;
  phone: string;
  mobile: string;
  preferredContact: string;
  website: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  source: string;
  sourceDetail: string;
  status: string;
  industryType: string;
  employeeCount: string;
  estimatedVolume: string;
  volumePeriod: string;
  estimatedValue: string;
  products: string;
  deliveryType: string;
  tankCount: string;
  tankCapacity: string;
  currentSupplier: string;
  currentPrice: string;
  contractEndDate: string;
  painPoints: string;
  priority: string;
  probability: string;
  expectedCloseDate: string;
  nextFollowUpDate: string;
  followUpNotes: string;
  notes: string;
  tags: string;
  assignedToId: string;
}

interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export default function LeadForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    contactFirstName: '',
    contactLastName: '',
    contactTitle: '',
    email: '',
    phone: '',
    mobile: '',
    preferredContact: 'EMAIL',
    website: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    source: 'OTHER',
    sourceDetail: '',
    status: 'NEW',
    industryType: '',
    employeeCount: '',
    estimatedVolume: '',
    volumePeriod: 'MONTHLY',
    estimatedValue: '',
    products: '',
    deliveryType: 'BULK',
    tankCount: '',
    tankCapacity: '',
    currentSupplier: '',
    currentPrice: '',
    contractEndDate: '',
    painPoints: '',
    priority: 'MEDIUM',
    probability: '',
    expectedCloseDate: '',
    nextFollowUpDate: '',
    followUpNotes: '',
    notes: '',
    tags: '',
    assignedToId: '',
  });

  // Fetch existing lead data if editing
  const { data: existingLead, isLoading: loadingLead } = useQuery({
    queryKey: ['lead', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/leads/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch lead');
      return res.json();
    },
    enabled: isEditing,
  });

  // Fetch users for assignment
  const { data: users } = useQuery<User[]>({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      return data.data || data;
    },
  });

  // Populate form when existing lead data loads
  useEffect(() => {
    if (existingLead) {
      setFormData({
        companyName: existingLead.companyName || '',
        contactFirstName: existingLead.contactFirstName || '',
        contactLastName: existingLead.contactLastName || '',
        contactTitle: existingLead.contactTitle || '',
        email: existingLead.email || '',
        phone: existingLead.phone || '',
        mobile: existingLead.mobile || '',
        preferredContact: existingLead.preferredContact || 'EMAIL',
        website: existingLead.website || '',
        address: existingLead.address || '',
        city: existingLead.city || '',
        state: existingLead.state || '',
        zip: existingLead.zip || '',
        source: existingLead.source || 'OTHER',
        sourceDetail: existingLead.sourceDetail || '',
        status: existingLead.status || 'NEW',
        industryType: existingLead.industryType || '',
        employeeCount: existingLead.employeeCount?.toString() || '',
        estimatedVolume: existingLead.estimatedVolume?.toString() || '',
        volumePeriod: existingLead.volumePeriod || 'MONTHLY',
        estimatedValue: existingLead.estimatedValue?.toString() || '',
        products: existingLead.products?.join(', ') || '',
        deliveryType: existingLead.deliveryType || 'BULK',
        tankCount: existingLead.tankCount?.toString() || '',
        tankCapacity: existingLead.tankCapacity?.toString() || '',
        currentSupplier: existingLead.currentSupplier || '',
        currentPrice: existingLead.currentPrice?.toString() || '',
        contractEndDate: existingLead.contractEndDate
          ? (new Date(existingLead.contractEndDate).toISOString().split('T')[0] ?? '')
          : '',
        painPoints: existingLead.painPoints || '',
        priority: existingLead.priority || 'MEDIUM',
        probability: existingLead.probability?.toString() || '',
        expectedCloseDate: existingLead.expectedCloseDate
          ? (new Date(existingLead.expectedCloseDate).toISOString().split('T')[0] ?? '')
          : '',
        nextFollowUpDate: existingLead.nextFollowUpDate
          ? (new Date(existingLead.nextFollowUpDate).toISOString().split('T')[0] ?? '')
          : '',
        followUpNotes: existingLead.followUpNotes || '',
        notes: existingLead.notes || '',
        tags: existingLead.tags?.join(', ') || '',
        assignedToId: existingLead.assignedToId || '',
      });
    }
  }, [existingLead]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const errorMessage = await parseApiError(res);
        throw new Error(errorMessage);
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate(`/sales/leads/${data.id}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await fetch(`${API_URL}/api/leads/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const errorMessage = await parseApiError(res);
        throw new Error(errorMessage);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate(`/sales/leads/${id}`);
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: Record<string, unknown> = {
      companyName: formData.companyName,
      contactFirstName: formData.contactFirstName || undefined,
      contactLastName: formData.contactLastName || undefined,
      contactTitle: formData.contactTitle || undefined,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      mobile: formData.mobile || undefined,
      preferredContact: formData.preferredContact || undefined,
      website: formData.website || undefined,
      address: formData.address || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      zip: formData.zip || undefined,
      source: formData.source,
      sourceDetail: formData.sourceDetail || undefined,
      industryType: formData.industryType || undefined,
      employeeCount: formData.employeeCount
        ? parseInt(formData.employeeCount, 10)
        : undefined,
      estimatedVolume: formData.estimatedVolume
        ? parseFloat(formData.estimatedVolume)
        : undefined,
      volumePeriod: formData.volumePeriod || undefined,
      estimatedValue: formData.estimatedValue
        ? parseFloat(formData.estimatedValue)
        : undefined,
      products: formData.products
        ? formData.products.split(',').map((p) => p.trim()).filter(Boolean)
        : undefined,
      deliveryType: formData.deliveryType || undefined,
      tankCount: formData.tankCount
        ? parseInt(formData.tankCount, 10)
        : undefined,
      tankCapacity: formData.tankCapacity
        ? parseFloat(formData.tankCapacity)
        : undefined,
      currentSupplier: formData.currentSupplier || undefined,
      currentPrice: formData.currentPrice
        ? parseFloat(formData.currentPrice)
        : undefined,
      contractEndDate: formData.contractEndDate
        ? new Date(formData.contractEndDate)
        : undefined,
      painPoints: formData.painPoints || undefined,
      priority: formData.priority || undefined,
      probability: formData.probability
        ? parseInt(formData.probability, 10)
        : undefined,
      expectedCloseDate: formData.expectedCloseDate
        ? new Date(formData.expectedCloseDate)
        : undefined,
      nextFollowUpDate: formData.nextFollowUpDate
        ? new Date(formData.nextFollowUpDate)
        : undefined,
      followUpNotes: formData.followUpNotes || undefined,
      notes: formData.notes || undefined,
      tags: formData.tags
        ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : undefined,
      assignedToId: formData.assignedToId || undefined,
    };

    // Include status only when editing
    if (isEditing) {
      data.status = formData.status;
    }

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  if (isEditing && loadingLead) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={isEditing ? `/sales/leads/${id}` : '/sales/leads'}
          className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-100">
            {isEditing ? 'Edit Lead' : 'New Lead'}
          </h1>
          <p className="text-slate-400">
            {isEditing ? `Editing ${existingLead?.leadNumber}` : 'Create a new sales lead'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">
            Company Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="md:col-span-2 lg:col-span-2">
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Company Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Employee Count
              </label>
              <input
                type="number"
                name="employeeCount"
                value={formData.employeeCount}
                onChange={handleChange}
                placeholder="0"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Industry Type
              </label>
              <select
                name="industryType"
                value={formData.industryType}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="">Select Industry</option>
                <option value="CONSTRUCTION">Construction</option>
                <option value="AGRICULTURE">Agriculture</option>
                <option value="TRANSPORTATION">Transportation</option>
                <option value="MANUFACTURING">Manufacturing</option>
                <option value="MINING">Mining</option>
                <option value="LOGISTICS">Logistics</option>
                <option value="GOVERNMENT">Government</option>
                <option value="RETAIL">Retail</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Website
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">
            Contact Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              {/* Split — lead lists sort by surname. */}
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Contact First Name
              </label>
              <input
                type="text"
                name="contactFirstName"
                value={formData.contactFirstName}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Contact Last Name
              </label>
              <input
                type="text"
                name="contactLastName"
                value={formData.contactLastName}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Title
              </label>
              <input
                type="text"
                name="contactTitle"
                value={formData.contactTitle}
                onChange={handleChange}
                placeholder="e.g., Purchasing Manager"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Preferred Contact
              </label>
              <select
                name="preferredContact"
                value={formData.preferredContact}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="EMAIL">Email</option>
                <option value="PHONE">Phone</option>
                <option value="MOBILE">Mobile</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Mobile
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Street Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">
                  ZIP
                </label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Lead Details */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Lead Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Source
              </label>
              <select
                name="source"
                value={formData.source}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                {LEAD_SOURCES.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Source Detail
              </label>
              <input
                type="text"
                name="sourceDetail"
                value={formData.sourceDetail}
                onChange={handleChange}
                placeholder="e.g., John Smith, Google Ads"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            {isEditing && (
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                >
                  {LEAD_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Assigned To
              </label>
              <select
                name="assignedToId"
                value={formData.assignedToId}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="">Unassigned</option>
                {users?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Win Probability (%)
              </label>
              <input
                type="number"
                name="probability"
                value={formData.probability}
                onChange={handleChange}
                min="0"
                max="100"
                placeholder="0-100"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </div>

        {/* Fuel Requirements */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Fuel Requirements</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Estimated Volume
              </label>
              <input
                type="number"
                name="estimatedVolume"
                value={formData.estimatedVolume}
                onChange={handleChange}
                placeholder="0"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Volume Period
              </label>
              <select
                name="volumePeriod"
                value={formData.volumePeriod}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="ANNUALLY">Annually</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Delivery Type
              </label>
              <select
                name="deliveryType"
                value={formData.deliveryType}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="BULK">Bulk Delivery</option>
                <option value="CARDLOCK">Cardlock</option>
                <option value="BOTH">Both</option>
              </select>
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Interested Products
              </label>
              <input
                type="text"
                name="products"
                value={formData.products}
                onChange={handleChange}
                placeholder="Diesel, Gasoline, Heating Oil (comma-separated)"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Tank Count
              </label>
              <input
                type="number"
                name="tankCount"
                value={formData.tankCount}
                onChange={handleChange}
                placeholder="0"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Total Tank Capacity (gallons)
              </label>
              <input
                type="number"
                name="tankCapacity"
                value={formData.tankCapacity}
                onChange={handleChange}
                placeholder="0"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </div>

        {/* Competition */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Competition</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Current Supplier
              </label>
              <input
                type="text"
                name="currentSupplier"
                value={formData.currentSupplier}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Current Price ($/gal)
              </label>
              <input
                type="number"
                step="0.01"
                name="currentPrice"
                value={formData.currentPrice}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Contract Expires
              </label>
              <input
                type="date"
                name="contractEndDate"
                value={formData.contractEndDate}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Pain Points
              </label>
              <textarea
                name="painPoints"
                value={formData.painPoints}
                onChange={handleChange}
                rows={2}
                placeholder="e.g., Delivery reliability, pricing, service quality..."
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </div>

        {/* Deal Information */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Deal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Estimated Value ($)
              </label>
              <input
                type="number"
                name="estimatedValue"
                value={formData.estimatedValue}
                onChange={handleChange}
                placeholder="0.00"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Expected Close Date
              </label>
              <input
                type="date"
                name="expectedCloseDate"
                value={formData.expectedCloseDate}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </div>

        {/* Follow-up */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Follow-up</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Next Follow-up Date
              </label>
              <input
                type="date"
                name="nextFollowUpDate"
                value={formData.nextFollowUpDate}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Follow-up Notes
              </label>
              <input
                type="text"
                name="followUpNotes"
                value={formData.followUpNotes}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </div>

        {/* Notes & Tags */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Notes & Tags</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Tags
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="high-priority, fleet, construction (comma-separated)"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to={isEditing ? `/sales/leads/${id}` : '/sales/leads'}
            className="px-6 py-2 text-slate-400 hover:text-slate-200"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isLoading || !formData.companyName}
            className="flex items-center gap-2 px-6 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditing ? 'Update Lead' : 'Create Lead'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
